import Dexie from 'dexie';
import axios from 'axios';
import config from './config';

export const db = new Dexie('OphthaDB');

// Enable verbose debugging for Dexie
if (process.env.NODE_ENV === 'development') {
  Dexie.debug = true;
}

db.version(9).stores({
  patients: 'id, other_id, age, sex, createdAt, syncStatus, optometrist_id',
  reports: 'id, patient_id, optometrist_id, left_image, right_image, left_image_hash, right_image_hash, ml_model, model_training_consent, diabetes, vision_issues, fundus_camera, single_image_reason, createdAt, syncStatus',
  // models table removed - using OPFS only for model storage
  dashboard_stats: 'id, data, lastUpdated, optometrist_id', // dashboard statistics
  user_credentials: 'id, username, password_hash, token, user_profile, optometrist_role, lastLogin, isOfflineEnabled', // offline login credentials
  image_hashes: 'hash, optometrist_id, patient_id, eye, created_at, synced', // image hash storage for duplicate detection
  fundus_cameras: 'facility_id, cameras, lastUpdated' // fundus camera list per facility (persists across logout/login)
});

// Single draft for screening images (reload recovery). One row only; overwritten each time. Cleared on submit/exit/new patient.
db.version(10).stores({
  screening_image_draft: 'id'
});

// Device-scoped key/value (survives logout; localStorage is cleared on logout)
db.version(11).stores({
  app_meta: 'key'
});

// Monitor database state
let dbState = 'unknown';

// Detect Safari mobile + network IP scenario (known IndexedDB issues)
const isSafariMobileNetworkIP = () => {
  if (typeof window === 'undefined') return false;
  
  const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                         /iPhone|iPad|iPod/i.test(navigator.userAgent);
  
  // Check if accessing via network IP (not localhost) over HTTP
  const hostname = window.location.hostname;
  const isNetworkIP = hostname !== 'localhost' && 
                      hostname !== '127.0.0.1' &&
                      // IP addresses contain dots, domain names also contain dots
                      // So we check if it's an IP pattern (numbers and dots) or a domain
                      (hostname.match(/^\d+\.\d+\.\d+\.\d+$/) || // IPv4 pattern
                       !hostname.includes('.')) && // No dots means likely IP without domain
                      window.location.protocol === 'http:'; // HTTP not HTTPS
  
  const result = isSafariMobile && isNetworkIP;
  
  return result;
};

// Initialize database and log status
// Note: Dexie v4 doesn't support db.on() events, so we use manual checking
const initializeDatabase = async () => {
  try {
    if (!db.isOpen()) {
      await db.open();
      dbState = 'open';
    } else {
      dbState = 'open';
    }
  } catch (error) {
    dbState = 'error';
  }
};

// Initialize database on module load (non-blocking)
initializeDatabase().catch(err => {
  // Failed to initialize database
});
const checkDbState = async () => {
  try {
    if (db.isOpen()) {
      dbState = 'open';
    } else {
      dbState = 'closed';
    }
  } catch (error) {
    dbState = 'error';
  }
};

// Check state periodically in development
if (process.env.NODE_ENV === 'development') {
  setInterval(checkDbState, 5000); // Check every 5 seconds
}

// Add connection retry mechanism
let connectionRetryCount = 0;
const maxRetries = 3;

// Export retryConnection for use in other modules
export const retryConnection = async (operation, ...args) => {
  const operationName = operation.name || 'unknown';
  
  try {
    // Ensure database is open before operation
    if (!db.isOpen()) {
      try {
        await db.open();
        dbState = 'open';
      } catch (openError) {
        dbState = 'error';
        throw new Error(`Database not open and failed to open: ${openError.message}`);
      }
    }
    
    const result = await operation(...args);
    connectionRetryCount = 0; // Reset retry count on success
    return result;
  } catch (error) {
    // Log specific error types
    if (error.name === 'QuotaExceededError') {
      
      // Detect Safari mobile for better error messaging
      const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                             /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      // Capture quota exceeded errors in Sentry (critical - user can't save data)
      import('@sentry/react').then(Sentry => {
        Sentry.captureException(error, {
          tags: {
            errorType: 'indexeddb_quota_exceeded',
            browser: isSafariMobile ? 'safari_mobile' : 'other',
          },
          extra: {
            userAgent: navigator.userAgent,
            storageEstimate: navigator.storage?.estimate ? 'available' : 'not_available',
          },
          level: 'error',
        });
      });
      
      // Create a more user-friendly error message for Safari mobile
      const errorMessage = isSafariMobile
        ? 'Storage is full. Please sync your data or clear browser storage in Settings > Safari > Clear History and Website Data.'
        : 'Storage quota exceeded. Please sync your data or clear some space.';
      
      // Throw error with user-friendly message
      const quotaError = new Error(errorMessage);
      quotaError.name = 'QuotaExceededError';
      quotaError.originalError = error;
      throw quotaError;
    } else if (error.name === 'InvalidStateError') {
      // Invalid state - database connection was lost
    } else if (error.name === 'TransactionInactiveError') {
      // Transaction inactive - transaction was closed
    } else if (error.name === 'UnknownError' || error.name === 'AbortError') {
      // Use the helper function to detect Safari mobile + network IP
      const safariMobileIssue = isSafariMobileNetworkIP();
      
      if (safariMobileIssue && connectionRetryCount === 0) {
        // Try to close and reopen the database connection (Safari mobile workaround)
        try {
          if (db.isOpen()) {
            db.close();
          }
          dbState = 'closed';
          
          // Wait longer for Safari to release the connection (Safari needs more time)
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Reopen database
          await db.open();
          dbState = 'open';
          
          // Reset retry count since we've done a workaround
          connectionRetryCount = 0;
          
          // Try the operation again
          const result = await operation(...args);
          return result;
        } catch (recreateError) {
          dbState = 'error';
          // Continue with normal retry logic below
        }
      }
    }
    
    // Check if error is retryable
    const isRetryableError = 
      error.name === 'InvalidStateError' || 
      error.name === 'TransactionInactiveError' ||
      error.name === 'UnknownError' ||
      error.name === 'AbortError' ||
      error.message?.includes('connection') ||
      error.message?.includes('server lost') ||
      error.message?.includes('Connection to Indexed Database server lost') ||
      error.message?.includes('internal error was encountered');
    
    if (connectionRetryCount < maxRetries && isRetryableError) {
      connectionRetryCount++;
      
      // Wait before retry (exponential backoff)
      const waitTime = 1000 * connectionRetryCount;
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Try to close and reopen the database
      try {
        // Close if open
        if (db.isOpen()) {
          db.close();
          dbState = 'closed';
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Reopen
        await db.open();
        dbState = 'open';
        return await operation(...args);
      } catch (retryError) {
        if (connectionRetryCount >= maxRetries) {
          // Detect Safari mobile + network IP scenario
          const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                                 /iPhone|iPad|iPod/i.test(navigator.userAgent);
          const isNetworkIP = window.location.hostname !== 'localhost' && 
                              window.location.hostname !== '127.0.0.1' &&
                              !window.location.hostname.includes('.');
          
          // Capture persistent IndexedDB connection errors
          import('@sentry/react').then(Sentry => {
            Sentry.captureException(error, {
              tags: {
                errorType: 'indexeddb_connection_failed',
                retryCount: connectionRetryCount,
                safariMobile: isSafariMobile,
                networkIP: isNetworkIP,
              },
              level: 'error',
            });
          });
          
          // Provide user-friendly error message
          let errorMessage = 'IndexedDB connection lost. ';
          if (isSafariMobile && isNetworkIP) {
            errorMessage += 'Safari mobile has limitations when accessing over network. ';
            errorMessage += 'Please try: 1) Refresh the page, 2) Use Chrome/Firefox browser, or 3) Access via HTTPS.';
          } else {
            errorMessage += 'Please refresh the page and try again.';
          }
          
          const finalError = new Error(errorMessage);
          finalError.name = error.name;
          finalError.originalError = error;
          throw finalError;
        }
        return retryConnection(operation, ...args);
      }
    } else {
      // Capture other IndexedDB errors
      if (error.name !== 'QuotaExceededError') {
        import('@sentry/react').then(Sentry => {
          Sentry.captureException(error, {
            tags: {
              errorType: 'indexeddb_error',
              errorName: error.name,
            },
            level: 'warning',
          });
        });
      }
      throw error;
    }
  }
};

// Utility function to get current user ID
export const getCurrentUserId = () => {
  try {
    const userProfile = JSON.parse(localStorage.getItem('user_profile'));
    return userProfile?.id;
  } catch (error) {
    console.error('Error getting current user ID:', error);
    return null;
  }
};


// Password hashing utility for offline authentication
export const hashPassword = async (password) => {
  try {
    // Check if crypto.subtle is available (not available in Safari on iPhone)
    if (!crypto.subtle) {
      console.warn('crypto.subtle not available, using fallback hashing');
      // Simple fallback hash for Safari compatibility
      let hash = 0;
      for (let i = 0; i < password.length; i++) {
        const char = password.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash).toString(16);
    }
    
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Error hashing password:', error);
    // Fallback to simple hash if crypto fails
    let hash = 0;
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }
};

// Utility function to get current user's data only
export const getCurrentUserPatients = async () => {
  const userId = getCurrentUserId();
  if (!userId) return [];
  
  try {
    return await retryConnection(async () => {
      return await db.patients.where('optometrist_id').equals(userId).toArray();
    });
  } catch (error) {
    console.error('Error getting current user patients:', error);
    return [];
  }
};

export const getCurrentUserReports = async () => {
  const userId = getCurrentUserId();
  if (!userId) return [];
  
  try {
    return await retryConnection(async () => {
      return await db.reports.where('optometrist_id').equals(userId).toArray();
    });
  } catch (error) {
    console.error('Error getting current user reports:', error);
    return [];
  }
};

// Utility function to get current user's pending (unsynced) data only
export const getCurrentUserPendingPatients = async () => {
  const userId = getCurrentUserId();
  if (!userId) return [];
  
  try {
    // Get all patients for current user, then filter by syncStatus
    const allPatients = await retryConnection(async () => {
      return await db.patients.where('optometrist_id').equals(userId).toArray();
    });
    return allPatients.filter(patient => patient.syncStatus === 'pending');
  } catch (error) {
    console.error('Error getting current user pending patients:', error);
    return [];
  }
};

export const getCurrentUserPendingReports = async () => {
  const userId = getCurrentUserId();
  if (!userId) return [];
  
  try {
    // Get all reports for current user, then filter by syncStatus
    const allReports = await retryConnection(async () => {
      return await db.reports.where('optometrist_id').equals(userId).toArray();
    });
    return allReports.filter(report => report.syncStatus === 'pending');
  } catch (error) {
    console.error('Error getting current user pending reports:', error);
    return [];
  }
};

export const getCurrentUserDashboardStats = async () => {
  const userId = getCurrentUserId();
  if (!userId) return null;
  
  try {
    return await retryConnection(async () => {
      return await db.dashboard_stats.where('optometrist_id').equals(userId).first();
    });
  } catch (error) {
    console.error('Error getting current user dashboard stats:', error);
    return null;
  }
};

// Utility function to clear all IndexedDB data
export const clearAllIndexedDBData = async () => {
  try {
    await retryConnection(async () => {
      await db.patients.clear();
      await db.reports.clear();
      await db.dashboard_stats.clear();
      await db.user_credentials.clear();
      await db.image_hashes.clear();
      await db.fundus_cameras.clear();
      // Note: models table removed - models are stored in OPFS only
    });
  } catch (error) {
    console.error('Error clearing IndexedDB data:', error);
  }
};

/**
 * Validate that a patient exists before saving a report (Foreign Key Validation)
 * @param {string} patientId - The patient ID to validate
 * @returns {Promise<boolean>} - True if patient exists, false otherwise
 */
export const validatePatientExists = async (patientId) => {
  try {
    if (!patientId) {
      return false;
    }
    const patient = await retryConnection(async () => {
      return await db.patients.get(patientId);
    });
    return !!patient;
  } catch (error) {
    console.error('Error validating patient exists:', error);
    return false;
  }
};

/**
 * Save patient and report together in a transaction (Atomic Operation)
 * This prevents orphaned reports if patient save fails
 * @param {Object} patientData - Patient data to save
 * @param {Object} reportData - Report data to save
 * @returns {Promise<{success: boolean, patientId: string, reportId: string, error?: string}>}
 */
export const savePatientAndReportTransaction = async (patientData, reportData) => {
  try {
    return await retryConnection(async () => {
      // Use Dexie transaction to ensure atomicity
      return await db.transaction('rw', db.patients, db.reports, async () => {
        // First, validate patient data
        if (!patientData || !patientData.id) {
          throw new Error('Patient data is invalid: missing id');
        }
        
        // Check if patient already exists (put() will update if exists, but we want to ensure it exists)
        const existingPatient = await db.patients.get(patientData.id);
        if (!existingPatient) {
          // Patient doesn't exist, save it
          await db.patients.put(patientData);
        } else {
          // Patient exists, update it (in case data changed)
          await db.patients.put(patientData);
        }
        
        // Validate patient was saved
        const savedPatient = await db.patients.get(patientData.id);
        if (!savedPatient) {
          throw new Error('Failed to save patient - patient not found after save');
        }
        
        // Validate report data has valid patient_id
        if (!reportData || !reportData.patient_id) {
          throw new Error('Report data is invalid: missing patient_id');
        }
        
        // Ensure report patient_id matches saved patient id
        if (reportData.patient_id !== patientData.id) {
          throw new Error(`Report patient_id (${reportData.patient_id}) does not match patient id (${patientData.id})`);
        }
        
        // Save report
        if (reportData.id) {
          await db.reports.put(reportData);
        } else {
          throw new Error('Report data is invalid: missing id');
        }
        
        // Validate report was saved
        const savedReport = await db.reports.get(reportData.id);
        if (!savedReport) {
          throw new Error('Failed to save report - report not found after save');
        }
        
        return {
          success: true,
          patientId: patientData.id,
          reportId: reportData.id
        };
      });
    });
  } catch (error) {
    console.error('Error in savePatientAndReportTransaction:', error);
    return {
      success: false,
      patientId: patientData?.id || null,
      reportId: reportData?.id || null,
      error: error.message
    };
  }
};

/**
 * Update report with validation (ensures patient exists)
 * @param {string} reportId - Report ID to update
 * @param {Object} updateData - Data to update
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateReportWithValidation = async (reportId, updateData) => {
  try {
    return await retryConnection(async () => {
      // If patient_id is being updated, validate it exists
      if (updateData.patient_id) {
        const patientExists = await validatePatientExists(updateData.patient_id);
        if (!patientExists) {
          throw new Error(`Cannot update report: patient with id ${updateData.patient_id} does not exist`);
        }
      }
      
      // Update report
      await db.reports.update(reportId, updateData);
      
      return { success: true };
    });
  } catch (error) {
    console.error('Error in updateReportWithValidation:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete patient with cascade deletion (deletes all associated reports)
 * @param {string} patientId - Patient ID to delete
 * @returns {Promise<{success: boolean, deletedReports: number, error?: string}>}
 */
export const deletePatientWithCascade = async (patientId) => {
  try {
    return await retryConnection(async () => {
      // Use transaction to ensure atomicity
      return await db.transaction('rw', db.patients, db.reports, async () => {
        // Find all reports for this patient
        const patientReports = await db.reports.where('patient_id').equals(patientId).toArray();
        
        // Delete all reports first
        const reportIds = patientReports.map(r => r.id);
        if (reportIds.length > 0) {
          await db.reports.bulkDelete(reportIds);
        }
        
        // Delete patient
        await db.patients.delete(patientId);
        
        return {
          success: true,
          deletedReports: reportIds.length
        };
      });
    });
  } catch (error) {
    console.error('Error in deletePatientWithCascade:', error);
    return {
      success: false,
      deletedReports: 0,
      error: error.message
    };
  }
};

const SCREENING_IMAGE_DRAFT_ID = 'current';

/**
 * Save screening images for reload recovery. Single record; overwritten each time.
 * Same format as report images: { data: ArrayBuffer, type, name }.
 * Clear on submit (online/offline), exit, Add New Patient, or new screening without patient_form_draft.
 * Only restored when patient_form_draft exists in localStorage (in-progress screening).
 */
export const saveScreeningImageDraft = async ({ patient_id, report_id, left_image, right_image }) => {
  try {
    if (!db.screening_image_draft) return;
    await db.screening_image_draft.put({
      id: SCREENING_IMAGE_DRAFT_ID,
      patient_id: patient_id || null,
      report_id: report_id || null,
      left_image: left_image || null,
      right_image: right_image || null,
      savedAt: new Date().toISOString()
    });
  } catch (err) {
    // Best-effort; don't break main flow
  }
};

/**
 * Get the current screening image draft, if any.
 */
export const getScreeningImageDraft = async () => {
  try {
    if (!db.screening_image_draft) return null;
    return await db.screening_image_draft.get(SCREENING_IMAGE_DRAFT_ID);
  } catch (err) {
    return null;
  }
};

/**
 * Clear screening image draft (on submit, exit, or Add New Patient).
 */
export const clearScreeningImageDraft = async () => {
  try {
    if (!db.screening_image_draft) return;
    await db.screening_image_draft.delete(SCREENING_IMAGE_DRAFT_ID);
  } catch (err) {
    // Ignore
  }
};

/**
 * Cleanup orphaned reports (reports with invalid patient_id)
 * @param {string} optometristId - Optional: only cleanup reports for specific optometrist
 * @returns {Promise<{success: boolean, deletedCount: number, orphanedReports: Array, error?: string}>}
 */
export const cleanupOrphanedReports = async (optometristId = null) => {
  try {
    return await retryConnection(async () => {
      // Get all reports (or filtered by optometrist)
      let allReports;
      if (optometristId) {
        allReports = await db.reports.where('optometrist_id').equals(optometristId).toArray();
      } else {
        allReports = await db.reports.toArray();
      }
      
      // Get all patient IDs
      const allPatients = await db.patients.toArray();
      const patientIds = new Set(allPatients.map(p => p.id));
      
      // Find orphaned reports (reports with patient_id that doesn't exist)
      const orphanedReports = allReports.filter(report => {
        return !report.patient_id || !patientIds.has(report.patient_id);
      });
      
      if (orphanedReports.length === 0) {
        return {
          success: true,
          deletedCount: 0,
          orphanedReports: []
        };
      }
      
      // Delete orphaned reports
      const orphanedReportIds = orphanedReports.map(r => r.id);
      await db.reports.bulkDelete(orphanedReportIds);
      
      return {
        success: true,
        deletedCount: orphanedReports.length,
        orphanedReports: orphanedReports.map(r => ({
          id: r.id,
          patient_id: r.patient_id,
          createdAt: r.createdAt || r.created_at
        }))
      };
    });
  } catch (error) {
    console.error('Error in cleanupOrphanedReports:', error);
    return {
      success: false,
      deletedCount: 0,
      orphanedReports: [],
      error: error.message
    };
  }
};

// Offline Login Functions
export const saveUserCredentials = async (username, password, token, userProfile, optometristRole) => {
  try {
    const passwordHash = await hashPassword(password);
    const credentials = {
      id: 'offline_user',
      username: username,
      password_hash: passwordHash,
      token: token,
      user_profile: JSON.stringify(userProfile),
      optometrist_role: optometristRole,
      lastLogin: new Date().toISOString(),
      isOfflineEnabled: true
    };
    
    await retryConnection(async () => {
      await db.user_credentials.put(credentials);
    });
    return true;
  } catch (error) {
    console.error('Error saving user credentials:', error);
    return false;
  }
};

export const getStoredCredentials = async () => {
  try {
    return await retryConnection(async () => {
      return await db.user_credentials.get('offline_user');
    });
  } catch (error) {
    console.error('Error getting stored credentials:', error);
    return null;
  }
};

export const clearStoredCredentials = async () => {
  try {
    await retryConnection(async () => {
      await db.user_credentials.delete('offline_user');
    });
    return true;
  } catch (error) {
    console.error('Error clearing stored credentials:', error);
    return false;
  }
};

/** Keep in sync with modelService `MODEL_VERSION_STORAGE_KEY` (legacy localStorage key). */
const APP_META_MODEL_DOWNLOAD_VERSION_KEY = 'optha_model_download_version';

export const getPersistedModelDownloadVersion = async () => {
  try {
    const row = await retryConnection(async () => {
      return await db.app_meta.get(APP_META_MODEL_DOWNLOAD_VERSION_KEY);
    });
    return row?.value ?? null;
  } catch (error) {
    console.error('Error reading model download version:', error);
    return null;
  }
};

export const setPersistedModelDownloadVersion = async (version) => {
  if (version == null || version === '') return;
  try {
    await retryConnection(async () => {
      await db.app_meta.put({
        key: APP_META_MODEL_DOWNLOAD_VERSION_KEY,
        value: String(version),
      });
    });
  } catch (error) {
    console.error('Error saving model download version:', error);
  }
};

/**
 * Before OPFS-only, large model buffers could live in a Dexie `models` table.
 * Current schema (v9+) has no `models` store — this is a no-op for most users.
 * Call when OPFS model bundle is purged so any surviving legacy rows are removed.
 */
export const clearLegacyIndexedDbModelBlobs = async () => {
  try {
    await db.open();
    const names = db.tables.map((t) => t.name);
    if (!names.includes('models')) {
      return;
    }
    await retryConnection(async () => {
      await db.models.clear();
    });
  } catch (e) {
    console.warn('[db] clearLegacyIndexedDbModelBlobs:', e?.message || e);
  }
};

export const isOfflineLoginAvailable = async () => {
  try {
    const credentials = await getStoredCredentials();
    return credentials && credentials.isOfflineEnabled;
  } catch (error) {
    console.error('Error checking offline login availability:', error);
    return false;
  }
};

// Validate password for offline login
export const validateOfflinePassword = async (username, password) => {
  try {
    const credentials = await getStoredCredentials();
    if (!credentials) {
      return false;
    }
    
    // Check username matches
    if (credentials.username !== username) {
      return false;
    }
    
    // Hash the provided password and compare with stored hash
    const providedPasswordHash = await hashPassword(password);
    return credentials.password_hash === providedPasswordHash;
  } catch (error) {
    console.error('Error validating offline password:', error);
    return false;
  }
};

// Fundus Camera List Functions (persist across logout/login)
export const saveFundusCameraList = async (facilityId, cameraList) => {
  try {
    await retryConnection(async () => {
      await db.fundus_cameras.put({
        facility_id: facilityId,
        cameras: cameraList,
        lastUpdated: new Date().toISOString()
      });
    });
    return true;
  } catch (error) {
    console.error('Error saving fundus camera list:', error);
    return false;
  }
};

export const getFundusCameraList = async (facilityId) => {
  try {
    const stored = await retryConnection(async () => {
      return await db.fundus_cameras.get(facilityId);
    });
    return stored || null; // ✅ RETURN FULL OBJECT
  } catch (error) {
    console.error("❌ Error getting fundus camera list:", error);
    return null;
  }
};

// Get last sync time for dashboard
export const getLastSyncTime = async () => {
  try {
    const dashboardStats = await getCurrentUserDashboardStats();
    return dashboardStats ? dashboardStats.lastUpdated : null;
  } catch (error) {
    console.error('Error getting last sync time:', error);
    return null;
  }
};

// Image Hash Management Functions
export const loadImageHashesFromBackend = async () => {
  try {
    const header = { "Authorization": "Token " + localStorage.getItem("optha-token") };
    const response = await axios.get(`${config.API_BASE_URL}/api/reports/image-hashes/`, { headers: header });
    
    // Handle different possible response structures
    const hashes = response.data.hashes || response.data || [];
    
    if (Array.isArray(hashes) && hashes.length > 0) {
      // Use bulkPut instead of bulkAdd to handle duplicates gracefully
      // bulkPut will update existing records or add new ones
      await db.image_hashes.bulkPut(hashes);
    }
    
    return true;
  } catch (error) {
    // Error loading image hashes from backend
    return false;
  }
};

export const saveImageHash = async (hash, patientId, eye) => {
  try {
    const userId = getCurrentUserId();
    
    if (!userId) {
      return false;
    }
    
    // Don't convert UUID patient IDs to numbers - keep them as strings
    // Allow both numeric and UUID patient IDs to be saved
    if (typeof patientId !== 'string' && typeof patientId !== 'number') {
      return false;
    }
    
    const hashData = {
      hash: hash,
      optometrist_id: userId, // Use same format as add report API
      patient_id: patientId, // Keep original patient ID (UUID or number)
      eye: eye,
      created_at: new Date().toISOString(),
      synced: false
    };
    
    await retryConnection(async () => {
      await db.image_hashes.put(hashData);
    });
    return true;
  } catch (error) {
    console.error('Error saving image hash:', error);
    return false;
  }
};

export const getUnsyncedImageHashes = async () => {
  try {
    const userId = getCurrentUserId();
    if (!userId) return [];
    
    return await retryConnection(async () => {
      return await db.image_hashes
        .where('optometrist_id').equals(userId)
        .and(hash => hash.synced === false)
        .toArray();
    });
  } catch (error) {
    console.error('Error getting unsynced image hashes:', error);
    return [];
  }
};

export const markImageHashesAsSynced = async (hashes) => {
  try {
    const hashValues = hashes.map(h => h.hash);
    
    await retryConnection(async () => {
      await db.image_hashes
        .where('hash').anyOf(hashValues)
        .modify({ synced: true });
    });
    
    return true;
  } catch (error) {
    console.error('Error marking image hashes as synced:', error);
    return false;
  }
};

// Example: patient object
// {
//   id: string (local UUID or backend ID),
//   age: number,
//   sex: string,
//   other_id: string,
//   optometrist_id: string, // user who created this patient
//   createdAt: string (ISO),
//   syncStatus: 'inprogress' | 'not_synced' | 'synced'
// }

// Example: report object
// {
//   id: string,
//   patient_id: string,
//   optometrist_id: string, // user who created this report
//   left_image: Blob,
//   right_image: Blob,
//   ml_model: object,
//   model_training_consent: boolean,
//   fundus_camera: string,
//   diabetes: string,
//   vision_issues: string,
//   single_image_reason: string,
//   createdAt: string (ISO)
// }

// Models are now stored in OPFS only, not in IndexedDB

// Example: dashboard_stats object
// {
//   id: string, // 'dashboard_stats'
//   data: object, // dashboard statistics from API
//   lastUpdated: string (ISO), // when the data was last updated
//   optometrist_id: string // user who owns these stats
// } 